'use client';

import { createElement, type MouseEvent, type ReactNode } from 'react';
import { AlertTriangle, Link2 } from 'lucide-react';
import { useParams, useRouter } from 'next/navigation';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  LIST_ROW_META_TEXT,
  ListRowGlyph,
  ListRowMarks,
  useListRowMarkPlacement,
  useListRowRhythm
} from '@/components/shared/list-row';
import { HandledByBadge } from '@/components/handling';
import { cn } from '@/lib/utils';
import type { InventoryItem } from '@/lib/services/inventory-items/types';
import {
  INVENTORY_STATUS_BADGE_QUIET,
  INVENTORY_STATUS_BADGE_TONES,
  INVENTORY_STATUS_QUIETED_BY_HANDOVER
} from '@/lib/services/inventory-items/status-badge-colors';
import { formatInventoryStatus } from '@/lib/services/inventory-items/utils';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { InstitutionLogo } from './institution-logo';
import { getTypeIcon, formatLineItemDisplay } from '../utils';

const RESOLUTION_PLAN_LABELS: Record<string, string> = {
  'property-sale': 'Property sale',
  'beneficiary-refinances': 'Beneficiary refinances',
  'vehicle-sale': 'Vehicle sale',
  'beneficiary-assumes': 'Beneficiary assumes',
  'estate-pays': 'Estate pays',
  undecided: 'Undecided'
};

function getSecuredHints(item: InventoryItem): {
  linkedAssetLabel: string | null;
  resolutionPlanLabel: string | null;
  decisionRequired: boolean;
  linkedAssetType: string | null;
  linkedAssetInventoryItemId: number | null;
} {
  const isLiability =
    item.category === 'liability' ||
    (typeof item.type === 'object' && item.type?.category === 'liability');
  if (!isLiability || item.linkedInventoryItemId == null) {
    return {
      linkedAssetLabel: null,
      resolutionPlanLabel: null,
      decisionRequired: false,
      linkedAssetType: null,
      linkedAssetInventoryItemId: null
    };
  }
  const linkedAssetLabel = item.linkedInventoryItem?.description ?? null;
  const plan = (item.itemInfo as { resolutionPlan?: string | null } | null)
    ?.resolutionPlan;
  const decisionRequired = plan == null || plan === 'undecided';
  const resolutionPlanLabel =
    plan && !decisionRequired ? (RESOLUTION_PLAN_LABELS[plan] ?? null) : null;
  return {
    linkedAssetLabel,
    resolutionPlanLabel,
    decisionRequired,
    linkedAssetType: item.linkedInventoryItem?.type ?? null,
    linkedAssetInventoryItemId: item.linkedInventoryItemId
  };
}

interface InventoryItemRowProps {
  item: InventoryItem;
  onClick?: (item: InventoryItem) => void;
  /**
   * Trailing per-row actions — the `⋯` menu (VALE-977). Optional, so the
   * matter Inventory tab's balance sheet is unchanged; the AI-extract review
   * passes one so a row can be dropped from the extract without opening it,
   * matching the Current View and Contacts rows that already carry one.
   *
   * The caller owns the stop-propagation wrapper, exactly as
   * `ContactRow`'s `trailing` does, so opening the menu does not also fire the
   * row's click.
   */
  trailing?: ReactNode;
  /**
   * **Whether the row states a figure at all** (VALE-1363). On by default,
   * which is the balance sheet proper.
   *
   * Off for the AI-extract review, where the figure is read in the detail block
   * under the row alongside every other extracted field — see
   * {@link InventoryItemRowProps.expanded} and `CompactInventoryDetail`. It
   * replaces VALE-977's `showCurrentValue`, whose only caller was that review.
   */
  showValue?: boolean;
  /** Whether to state the item's lifecycle status (VALE-1584). See `BalanceSheet`. */
  showStatus?: boolean;
  /**
   * An indented detail block revealed under the row (VALE-1181) — see
   * `CompactInventoryDetail`. Absent for every existing caller, which is what
   * keeps the matter Inventory tab byte-identical.
   *
   * **It sits inside the row's `group` wrapper but OUTSIDE the clickable row
   * line**, which is `ContactList`'s rule carried across (VALE-1196): the row
   * keeps its own hover and click, the block gets neither. The AI review puts
   * editable fields in that block, and wrapping both in one target made a click
   * meant for a field also open a drawer over it.
   */
  expanded?: ReactNode;
  /**
   * A badge stating what the SURFACE says about this row (VALE-1181) — the AI
   * review's NEW / UPDATED mark, an institution-match warning.
   *
   * **It renders after the row's own information, not in the state column
   * beside the `⋯`** (VALE-1196's placement, restored). Those pills describe
   * the record's own lifecycle and a reader scans the right edge for them;
   * this says something about the REVIEW the row is being read inside, so it
   * belongs next to the thing it qualifies.
   *
   * VALE-1725 standardised the pills into that column and this travelled with
   * them incidentally. **VALE-1654 and VALE-1737 both undid that, from
   * different directions, and the second subsumes the first**: VALE-1654 moved
   * this one badge back unconditionally, VALE-1737 made the whole cluster's
   * placement the LIST's call, so every mark on a review row lands beside the
   * text and every non-review list keeps the column. Where it sits is
   * therefore no longer this row's decision at all — see `ListRowMarks`.
   *
   * Worth knowing that this docblock described VALE-1196's placement for two
   * tickets after the code had stopped doing it, and a reader who trusted it
   * concluded the badge was on the left when it was already on the right. Keep
   * it in step with the JSX below.
   */
  infoBadge?: ReactNode;
  /**
   * Classes for this item's WHOLE entry — its row line and the block under it
   * (VALE-1181).
   *
   * For a state belonging to the record rather than to the row: the AI-extract
   * review greys an item it will not be adding. `ContactList.rowClassName` is
   * the same slot on the other half of that review.
   *
   * Named `itemClassName` rather than `className` because `BalanceSheetSection`
   * chooses between this component and the draggable one at a single JSX site,
   * so the two have to agree on the name — and the draggable already spends
   * `className` on its own drag affordances.
   */
  itemClassName?: string;
  /**
   * Wrap this row's own LINE (VALE-1181) — the AI review's provenance popover,
   * whose anchor has to be the row element itself.
   *
   * The block below is deliberately outside it: hovering a field there must not
   * hold the row's popover open, and those fields are editable.
   */
  rowWrapper?: (row: ReactNode) => ReactNode;
  /**
   * Parts of the row line the reviewer has turned down (VALE-1181), struck so
   * the row stops asserting a value that will not be written.
   *
   * The row draws the extract's PROPOSED values, so after a reject it is showing
   * something that is not going to happen — which is the one place a reviewer
   * scanning the list would notice it, and the detail block below is collapsed
   * out of view on nothing.
   */
  struck?: InventoryRowStruckParts;
  /**
   * What the SURFACE says about this row, as the lead of its secondary text,
   * ahead of the row's own detail. The estate-planning Inventory tab puts where
   * the asset goes here ("Joint with Geoffrey, goes to the survivor"). Absent
   * for every other caller, which keeps their rows unchanged.
   */
  note?: string;
}

/**
 * Which parts of a row line read as rejected (VALE-1181).
 *
 * Named here, where the parts are actually drawn, so the three components that
 * only forward it cannot describe a different set from the one that renders -
 * `detail` was added to the row and missed by all three while each held its own
 * copy of the shape.
 */
export interface InventoryRowStruckParts {
  title?: boolean;
  /**
   * Which of the title's keys were rejected (VALE-1375).
   *
   * Used only where `formatLineItemDisplay` split the title into segments, so a
   * multi-key title strikes the rejected part alone. `title` stays the answer
   * for every single-key title, and for a segmented one whose every key is
   * rejected.
   */
  titleKeys?: string[];
  /**
   * The institution's favicon, which is a SECOND place the row draws the
   * institution and needs its own part (VALE-1353).
   *
   * Not derivable from `title`: on the two share types the title is the ticker,
   * so rejecting the institution does not touch `title` at all. Keyed on
   * `institution`, where `title` is keyed on whatever `formatLineItemDisplay`
   * named the row after.
   */
  logo?: boolean;
  detail?: boolean;
  /*
    **The two figures were members here and are not any more** (VALE-1363).
    They were struck on the row line because the row line stated them; the only
    surface that both draws figures and offers a reject is the AI-extract
    review, and it no longer draws them — the detail block does, where the
    treatment's own rule 8 already strikes a rejected value where it renders it.

    A figure part cannot be reinstated on the strength of the row's `showValue`
    column either: that column is the balance sheet proper, which has no reject
    to strike from.
  */
}

function getItemTypeSlug(item: InventoryItem): string {
  return typeof item.type === 'string' ? item.type : item.type?.slug || 'other';
}

function TypeIconForSlug({
  typeSlug,
  className
}: {
  typeSlug: string;
  className?: string;
}) {
  return createElement(getTypeIcon(typeSlug), { className });
}

export function InventoryItemRow({
  item,
  onClick,
  trailing,
  showValue = true,
  showStatus = true,
  expanded,
  infoBadge,
  itemClassName,
  rowWrapper,
  struck,
  note
}: InventoryItemRowProps) {
  /*
    Row height, title weight and glyph size come from the enclosing surface's
    rhythm (VALE-1494) — see `shared/list-row`.
  */
  const rhythm = useListRowRhythm();
  const typeSlug = getItemTypeSlug(item);
  const { label, labelSegments, detail } = formatLineItemDisplay(
    typeSlug,
    item.institution?.name,
    item.itemInfo,
    item.description
  );
  const isCompleted = [
    'collected',
    'resolved',
    'archived',
    'settled',
    'not-owed'
  ].includes(item.status);
  const value = item.value ?? 0;
  const {
    linkedAssetLabel,
    resolutionPlanLabel,
    decisionRequired,
    linkedAssetType,
    linkedAssetInventoryItemId
  } = getSecuredHints(item);
  const router = useRouter();
  const params = useParams<{ matterId?: string }>();
  const matterId = params?.matterId ? Number(params.matterId) : null;
  const decisionTarget: 'propertyInstructions' | 'vehicleInstructions' | null =
    linkedAssetType === 'real-estate'
      ? 'propertyInstructions'
      : linkedAssetType === 'vehicle'
        ? 'vehicleInstructions'
        : null;
  const canDeepLink =
    decisionRequired &&
    matterId != null &&
    linkedAssetInventoryItemId != null &&
    decisionTarget != null;

  const handleDecide = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!canDeepLink) return;
    router.push(
      `/matters/${matterId}/redemption?${decisionTarget}=${linkedAssetInventoryItemId}`
    );
  };

  /*
    The row's own name, for the specs that count and open these rows
    (VALE-1080). They keyed off `div.cursor-pointer`, which is shared with the
    contact rows and the document cards that render beside this list on the
    create-matter review — so "how many inventory rows are there" had no honest
    answer there.
  */
  const marksAreInline = useListRowMarkPlacement() === 'inline';

  /*
    **The row's marks, rendered at ONE of two positions** (VALE-1737).

    Inline they sit inside the title block, right after the row's detail text —
    where they sat before VALE-1725, and where the AI-extract review wants them:
    an asset row there is a proposal, and `NEW` or `Check the institution` reads
    as part of what the row is saying rather than as a column beside it. On the
    `column` placement they render outside that block instead, right-aligned
    against the actions, which is VALE-1725's treatment and what the matter's
    own Inventory tab keeps.

    One node, two call sites, so the placements cannot drift into drawing
    different marks or a different order.
  */
  const marks = (
    <ListRowMarks testId="inventory-item-row-marks">
      {/*
            **Withheld where nothing has a lifecycle yet** (VALE-1584) — see
            `BalanceSheet`'s `showStatus`. Every other surface states it.
          */}
      {showStatus && (
        <StatusBadge
          tone={
            INVENTORY_STATUS_BADGE_TONES[item.status] ||
            INVENTORY_STATUS_BADGE_TONES.draft
          }
          /*
            A handover quiets a status that was loud only because the FIRM had to
            act (VALE-1854). Reads `effectiveHandling` for the same reason the
            badge below does: an asset at a handed-over institution carries no
            handler of its own, so the raw field is null for exactly the rows this
            is about. The label is untouched — only its claim on attention changes.
          */
          quiet={
            (INVENTORY_STATUS_BADGE_QUIET[item.status] ?? false) ||
            (!!(item.effectiveHandling ?? item.handling)?.handledBy &&
              (INVENTORY_STATUS_QUIETED_BY_HANDOVER[item.status] ?? false))
          }
          className="shrink-0"
        >
          {formatInventoryStatus(item.status)}
        </StatusBadge>
      )}
      {/*
            "Handled by [Name]" (EH-DI-002). Records surfaces render the badge and
            never filter on it (EH-006, EH-DI-001) — every row still shows.

            This reads the item's OWN handling, which the backend populates only
            for the institution-less types (EH-CR-007). An institution-backed item
            inherits from its institution; that resolution is VALE-839's single
            shared mechanism, so this surface deliberately does not compute a
            second one from `item.institution`.
          */}
      {/*
        `effectiveHandling`, not `handling`: an asset at a handed-over bank carries
        no handler of its own (a DB CHECK sees to that), so the raw field is null
        for exactly the rows this badge exists to mark (VALE-1760). Falls back to
        the item's own handler for the projections that do not resolve it.
      */}
      <HandledByBadge
        handling={item.effectiveHandling ?? item.handling}
        className="shrink-0"
      />
      {linkedAssetLabel && (
        <StatusBadge tone="neutral" icon={Link2} className="shrink-0">
          <span className="max-w-[16ch] truncate">
            Secured against {linkedAssetLabel}
          </span>
        </StatusBadge>
      )}
      {resolutionPlanLabel && (
        <StatusBadge tone="neutral" className="shrink-0">
          {resolutionPlanLabel}
        </StatusBadge>
      )}
      {decisionRequired &&
        (canDeepLink ? (
          // Interactive twin of the "Decision required" StatusBadge below —
          // a real control, so it stays a <button>. Its palette tracks the
          // `caution` tone ramp so the two reads identically (LF-6150).
          <button
            type="button"
            onClick={handleDecide}
            className="border-warning-subtle-border bg-warning-subtle text-warning-subtle-foreground hover:bg-warning-subtle-border focus:ring-warning inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-xs leading-none font-medium focus:ring-2 focus:outline-none"
          >
            <AlertTriangle className="h-3 w-3" />
            Set instructions
          </button>
        ) : (
          <StatusBadge tone="caution" icon={AlertTriangle} className="shrink-0">
            Decision required
          </StatusBadge>
        ))}
      {/*
          **Last in the cluster** (VALE-1737). The order down the column is the
          row's own state first and then what the surface says ABOUT the row —
          the institution-match warning, a proposed handover, and last the AI
          review's `NEW` / `UPDATED` tag (VALE-1995).
          It used to sit in the middle, between the resolution plan and
          `Decision required`, so a row with a decision outstanding put a
          record's state to the right of a review's mark while the row above it
          did the opposite.
        */}
      {infoBadge}
    </ListRowMarks>
  );

  const rowLine = (
    <div
      data-testid="inventory-item-row"
      className={cn(
        /*
          `gap-2`, the same gap `ContactRow` spends between its title block,
          its mark column and its actions (VALE-1737). These two rows sit one
          block above the other on every AI-extract review, and the separations
          between their three parts have to be one number or the marks and the
          `⋯` land in two columns down the page.
        */
        'flex items-center gap-2 px-4',
        /*
          A tighter bottom when this row's own detail block follows
          (VALE-1494) — that padding separates rows, and with the block there
          it would be separating the row from its own values.
        */
        expanded ? rhythm.paddingYWithDetail : rhythm.paddingY,
        /*
          **Derived from the handler, not hardcoded** (VALE-1699) — the twin of
          the same change on `ContactRow`, made together because these two rows
          sit side by side on every AI-extract review and a pointer on one but
          not the other would be a worse answer than either.

          Both were unconditional, so a list mounted without `onItemClick` drew
          a row that followed the cursor and lit up under it and then did
          nothing. `AttributeReadOut` refuses that lie one level down — a
          `readOnly` field is a `<span>`, not a `<button>`, so "the element
          itself carries the claim" — and a row owes the same honesty.

          The condition is the one this package already writes:
          `current-view/distributed-rows.tsx` and `beneficiary-cash-row.tsx`.

          The specs that count and open these rows key off this row's own
          `data-testid="inventory-item-row"` rather than the class (VALE-1080),
          so deriving it moves nothing for them.
        */
        onClick && 'hover:bg-surface-subtle cursor-pointer'
      )}
      onClick={() => onClick?.(item)}
    >
      {/*
        **`flex-1` and the right gutter used to belong to the note slot**
        (VALE-1377). That slot was this row's flexible middle column, so
        deleting it without moving its growth here would let the value cell and
        the `⋯` slide left into the badges.
      */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/*
            **A rejected institution gives the row its ASSET icon back**
            (VALE-1353), rather than a struck or faded favicon.

            The logo is the most recognisable thing on the row, so leaving it up
            after a reject is the row's loudest way of asserting a value that
            will not be written. On a shareholding it was the row's ONLY
            assertion of the institution, since the title there is the ticker.
            The honest rendering already exists and is the one below: an item
            with no institution shows what KIND of asset it is, and a reject
            means no institution is being written.

            **It erases rather than strikes, and that is the one thing this
            costs.** Every other rejected part of the row line stays legible
            with a line through it; this one leaves a row byte-identical to a
            row that never had an institution, so the row line alone no longer
            says a reject happened. Accepted deliberately: a struck or faded
            favicon is not a state this row has, and the detail block below is
            always open and carries both the strike and the undo.
          */}
        <ListRowGlyph>
          {item.institution?.faviconUrl && !struck?.logo ? (
            /* Sized with the type glyph it stands in for, so the two occupy
               the same box and the title column cannot step in and out down
               the list (VALE-1494). */
            <InstitutionLogo
              logo={item.institution.faviconUrl}
              className={rhythm.icon}
            />
          ) : (
            <TypeIconForSlug
              typeSlug={typeSlug}
              className={cn('text-foreground-subtle shrink-0', rhythm.icon)}
            />
          )}
        </ListRowGlyph>
        {/*
            Named separately from the row (VALE-1080) because the row's *title*
            is a rule of its own: it must be the institution where there is one,
            never the bare type (LF-4963). The detail span beside it legitimately
            carries the type, so an assertion over the whole row cannot tell the
            two apart.
          */}
        <span
          data-testid="inventory-item-row-title"
          className={cn(
            'text-foreground truncate',
            rhythm.titleText,
            struck?.title && 'text-muted-foreground line-through opacity-70'
          )}
        >
          {/*
            **A multi-key title strikes only the part that was rejected**
            (VALE-1375). A vehicle's title is `Motor Vehicle - {make} {model}`,
            so a rejected model used to strike the make and the type with it,
            claiming the reviewer had turned down more than they had. Every
            other type names its row after a single key, has no segments, and
            keeps striking whole through `struck.title` above.
          */}
          {labelSegments && struck?.titleKeys?.length
            ? labelSegments.map((segment) => (
                <span
                  /* Segments are a pure function of the label, and no two carry
                     the same key-and-text pair, so this is stable without the
                     index. */
                  key={`${segment.key ?? 'fixed'}:${segment.text}`}
                  className={cn(
                    segment.key &&
                      struck.titleKeys?.includes(segment.key) &&
                      'text-muted-foreground line-through opacity-70'
                  )}
                >
                  {segment.text}
                </span>
              ))
            : label}
        </span>
        {(note || detail) && (
          <span
            className={cn(
              'text-muted-foreground shrink-[100] truncate',
              LIST_ROW_META_TEXT,
              struck?.detail && 'line-through opacity-70'
            )}
          >
            {[note, detail].filter(Boolean).join(' · ')}
          </span>
        )}
        {marksAreInline && marks}
      </div>

      {!marksAreInline && marks}

      {/*
        **The date-of-death figure, and it is the only one the row states**
        (VALE-1363). VALE-977's second cell was the AI-extract review's, and
        that review now reads both figures in the block below instead — so the
        row is a single-value table again, which is what the balance sheet
        proper has always been.
      */}
      {showValue && (
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-end gap-1 text-sm tabular-nums',
            isCompleted
              ? 'text-muted-foreground line-through'
              : 'text-foreground'
          )}
        >
          {value < 0 ? (
            <>
              {`(${formatCurrency(Math.abs(value))})`}
              {/* A negative liability value is a "credit" — the institution
                    owes the estate money — not a data-entry mistake (LF-5563). */}
              <StatusBadge tone="success" className="shrink-0">
                Credit
              </StatusBadge>
            </>
          ) : (
            formatCurrency(value)
          )}
        </span>
      )}
      {!!trailing && <div className="shrink-0">{trailing}</div>}
    </div>
  );

  return (
    <div className={cn('group', itemClassName)}>
      {rowWrapper ? rowWrapper(rowLine) : rowLine}
      {expanded}
    </div>
  );
}
